/**
 * PHSIMS Web Admin Frontend - Main Application
 * Author: Iswe Solutions Team
 * 
 * React Admin application for PHSIMS Lakehouse administration
 * Zambia National Public Health Institute (ZNPHI) Branding
 * 
 * All pages require authentication. Session validation occurs on each route.
 * 
 * Uses BrowserRouter for clean URLs (no /#/ hash prefix).
 */

// React must be imported first for emotion/MUI to work correctly
import React, { useState, useEffect, useCallback, useRef } from 'react';

import { Admin, Resource, Layout, CustomRoutes, useLogout, useGetIdentity, useSidebarState, Authenticated } from 'react-admin';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';

import { LaunchConfetti } from './components/common/LaunchConfetti';
import { 
  Box, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem, 
  Avatar, 
  Divider, 
  ListItemIcon, 
  ListItemText, 
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Snackbar,
  InputAdornment,
  Badge,
  Tooltip,
  List,
  ListItem,
  Popover,
  alpha,
  Chip,
  Slide,
  Paper,
} from '@mui/material';
import { Route, useNavigate, Navigate, useLocation } from 'react-router-dom';  // Route for CustomRoutes, useNavigate for navigation
import StorageIcon from '@mui/icons-material/Storage';
import DatasetIcon from '@mui/icons-material/Dataset';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import HistoryIcon from '@mui/icons-material/History';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LogoutIcon from '@mui/icons-material/Logout';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import LockIcon from '@mui/icons-material/Lock';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import zambiaTheme from './theme';
import dataProvider from './dataProvider';
import authProvider from './authProvider';
import { AppFooter } from './components/common/AppFooter';
import { apiClient } from './services/api';
import { startVersionChecker, restoreScrollPosition } from './utils/versionChecker';
import { IS_DEV_INSTANCE } from './utils/publicUrls';

// Lazy-load overlay components to avoid blocking initial render
const UpdateBanner = React.lazy(() => import('./components/common/UpdateBanner'));
const ConnectionStatusBanner = React.lazy(() => import('./components/common/ConnectionStatusBanner'));

// Core components - eager loaded (lightweight)
import LoginPage from './components/LoginPage';
import PasswordResetPage from './components/PasswordResetPage';
import ChangePasswordPage from './components/ChangePasswordPage';
import MfaSetupPage from './components/MfaSetupPage';
import { useIdleLogout } from './hooks/useIdleLogout';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import RoleBasedHome from './components/RoleBasedHome';
import UnifiedMenu from './components/UnifiedMenu';
import RequireRole from './components/common/RequireRole';

// Loading skeleton for lazy-loaded pages
import PageSkeleton, { 
  TableSkeleton, 
  ChatSkeleton, 
  FormSkeleton 
} from './components/common/PageSkeleton';

/**
 * LAZY-LOADED PAGE COMPONENTS
 * All heavy dashboard/page components use React.lazy for code-splitting.
 * This reduces initial bundle size and improves first-paint performance.
 */

// Data Management Pages
const SourcesDashboard = React.lazy(() => import('./components/SourcesDashboard'));
const SourceDetailsRouter = React.lazy(() => import('./components/SourceDetailsRouter'));
const OrgUnitHarmonization = React.lazy(() => import('./components/OrgUnitHarmonization'));
const UploadsPage = React.lazy(() => import('./components/uploads/UploadsPage'));

// Dashboard Pages
const ExtractionMonitorDashboard = React.lazy(() => import('./components/dashboards/ExtractionMonitorDashboard'));
const DataQualityDashboard = React.lazy(() => import('./components/dashboards/DataQualityDashboard'));
const PipelineDashboard = React.lazy(() => import('./components/dashboards/PipelineDashboard'));
const SystemHealthDashboard = React.lazy(() => import('./components/dashboards/SystemHealthDashboard'));
const SystemPulsePage = React.lazy(() => import('./components/dashboards/SystemPulsePage'));
const RedisMonitoringDashboard = React.lazy(() => import('./components/dashboards/RedisMonitoringDashboard'));
const ServicesDashboard = React.lazy(() => import('./components/dashboards/ServicesDashboard'));
const ErrorsDashboard = React.lazy(() => import('./components/dashboards/ErrorsDashboardOptimized'));
const APIGatewayDashboard = React.lazy(() => import('./pages/APIGatewayDashboard'));
const FederationDashboard = React.lazy(() => import('./pages/FederationDashboard'));
const MarketplacePage = React.lazy(() => import('./pages/MarketplacePage'));
const ConsumerDetailPage = React.lazy(() => import('./pages/ConsumerDetailPage'));
const ProductDetailPage = React.lazy(() => import('./pages/ProductDetailPage'));
const DeveloperPortalPage = React.lazy(() => import('./pages/DeveloperPortalPage'));
const CustomEndpointsPage = React.lazy(() => import('./pages/CustomEndpointsPage'));
const ConfigDashboard = React.lazy(() => import('./components/dashboards/ConfigDashboard'));
const PoolDashboard = React.lazy(() => import('./components/dashboards/PoolDashboard'));
const ScheduleManagerDashboard = React.lazy(() => import('./components/ScheduleManagerDashboard'));
const AuditLogDashboard = React.lazy(() => import('./components/dashboards/AuditLogDashboard'));

// Administration Pages
const SecurityManagement = React.lazy(() => import('./components/dashboards/SecurityManagement'));
const CredentialManagement = React.lazy(() => import('./components/dashboards/CredentialManagement'));

const LogsDashboard = React.lazy(() => import('./components/dashboards/LogsDashboardOptimized'));
const BackupDashboard = React.lazy(() => import('./components/dashboards/BackupDashboard'));
const AlertsConfiguration = React.lazy(() => import('./components/AlertsConfiguration'));
const EmailManagementPage = React.lazy(() => import('./components/EmailManagementPage'));

// SQL Explorer
const SqlExplorer = React.lazy(() => import('./components/explorer/SqlExplorerEnhanced'));

// AI Dashboard Components
const AIHomeDashboard = React.lazy(() => import('./components/ai/AIHomeDashboard'));
const ModernChat = React.lazy(() => import('./components/ai/ModernChat'));
const AIKnowledgePage = React.lazy(() => import('./components/ai/AIKnowledgePage'));
const AlertConfigPage = React.lazy(() => import('./pages/AlertConfigPage'));
const ModelTransparencyPage = React.lazy(() => import('./pages/ModelTransparencyPage'));
const ThresholdAlertProgramPage = React.lazy(() => import('./pages/ThresholdAlertProgramPage'));
const InvestigationWorkspace = React.lazy(() => import('./pages/InvestigationWorkspace'));
const HotspotAnalysisPage = React.lazy(() => import('./pages/HotspotAnalysisPage'));
const ResponseTrackerPage = React.lazy(() => import('./pages/ResponseTrackerPage'));
const ExcessMortalityPage = React.lazy(() => import('./pages/ExcessMortalityPage'));
const WeeklyBulletinPage = React.lazy(() => import('./pages/WeeklyBulletinPage'));
const BulletinGeneratorPage = React.lazy(() => import('./pages/BulletinGeneratorPage'));
const DataStudioPage = React.lazy(() => import('./pages/DataStudioPage'));
const DiseaseStatusPage = React.lazy(() => import('./pages/DiseaseStatusPage'));
const InfodemicReviewPage = React.lazy(() => import('./pages/InfodemicReviewPage'));
const LabAnalyticsPage = React.lazy(() => import('./pages/LabAnalyticsPage'));
const SystemConfigurationPage = React.lazy(() => import('./pages/SystemConfigurationPage'));
const OutliersPage = React.lazy(() => import('./pages/OutliersPage'));

// Analytics Engine Pages
const SignalDashboardPage = React.lazy(() => import('./pages/SignalDashboardPage'));
const AnalyticsExplorerPage = React.lazy(() => import('./pages/AnalyticsExplorerPage'));
const AgentMonitorPage = React.lazy(() => import('./pages/AgentMonitorPage'));
const EngineMonitoringPage = React.lazy(() => import('./pages/EngineMonitoringPage'));

// §9.7 Revised Sidebar — new routes
const DiseaseDeepDivePage = React.lazy(() => import('./pages/DiseaseDeepDivePage'));
const AlgorithmCatalogPage = React.lazy(() => import('./pages/AlgorithmCatalogPage'));
const ClimateDiseasePage = React.lazy(() => import('./pages/ClimateDiseasePage'));
const OneHealthPage = React.lazy(() => import('./pages/OneHealthPage'));
const MalariaIntelligencePage = React.lazy(() => import('./pages/MalariaIntelligencePage'));
const WeatherAnalyticsPage = React.lazy(() => import('./pages/WeatherAnalyticsPage'));

// Surveillance Dashboard — merged view of Overview + Diseases + Operations
const SurveillanceDashboardPage = React.lazy(() => import('./pages/SurveillanceDashboardPage'));
const LaunchPage = React.lazy(() => import('./pages/LaunchPage'));
const ExecutiveOverviewPage = React.lazy(() => import('./pages/ExecutiveOverviewPage'));

// Platform Components - AI Analytics
const AnomalyDetection = React.lazy(() => import('./components/platform/AnomalyDetection'));
const PublicDashboard = React.lazy(() => import('./components/platform/PublicDashboard'));
const TrendAnalysis = React.lazy(() => import('./pages/TrendAnalysisPage'));
const Predictions = React.lazy(() => import('./components/platform/Predictions'));
const Reports = React.lazy(() => import('./components/platform/Reports'));
const DataExplorer = React.lazy(() => import('./components/platform/DataExplorer'));
const DataDictionary = React.lazy(() => import('./components/platform/DataModelTree'));
const ReportBuilder = React.lazy(() => import('./components/analytics/ReportBuilder'));
const DataVisualizer = React.lazy(() => import('./components/analytics/DataVisualizer'));
const EventVisualizer = React.lazy(() => import('./components/analytics/EventVisualizer'));

// DISA Extractor Configuration
const DisaConfigDashboard = React.lazy(() => import('./components/disa/DisaConfigDashboard'));

// Analytics Overview - removed (dead code)

// Surveillance Hub - redirects to SurveillanceDashboardPage
// const SurveillanceHubPage - now embedded in SurveillanceDashboardPage

// Surveillance Intelligence - redirects to SurveillanceDashboardPage
// const SurveillanceIntelligencePage - now embedded in SurveillanceDashboardPage

// Operations Dashboard - System metrics for admins
const EnhancedDashboard = React.lazy(() => import('./components/EnhancedDashboard'));

// Infrastructure Pages
const WebTerminal = React.lazy(() => import('./components/infrastructure/WebTerminal'));
const FileBrowser = React.lazy(() => import('./components/infrastructure/FileBrowser'));
const DockerEvents = React.lazy(() => import('./components/infrastructure/DockerEvents'));
const ImageManagement = React.lazy(() => import('./components/infrastructure/ImageManagement'));

// Documentation Pages
const HelpPage = React.lazy(() => import('./components/docs/HelpPage'));
const TechnicalDocs = React.lazy(() => import('./components/docs/TechnicalDocs'));
const ApiReferencePage = React.lazy(() => import('./pages/ApiReferencePage'));

// §9.3 Floating AI Chat Panel — available on every page
const FloatingChatButton = React.lazy(() => import('./components/common/FloatingChatButton'));

/**
 * Suspense wrapper for lazy-loaded components with appropriate skeleton
 */
const LazyPage: React.FC<{ 
  children: React.ReactNode; 
  fallback?: React.ReactNode;
}> = ({ children, fallback = <PageSkeleton /> }) => (
  <React.Suspense fallback={fallback}>
    {children}
  </React.Suspense>
);

const LazyTable: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <React.Suspense fallback={<TableSkeleton />}>
    {children}
  </React.Suspense>
);

const LazyChat: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <React.Suspense fallback={<ChatSkeleton />}>
    {children}
  </React.Suspense>
);

const LazyForm: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <React.Suspense fallback={<FormSkeleton />}>
    {children}
  </React.Suspense>
);

/**
 * Lazy-wrapped components for React Admin Resources
 * These are required because Resource.list expects a component, not a JSX element
 */
const LazySourcesDashboard = () => (
  <React.Suspense fallback={<PageSkeleton />}>
    <SourcesDashboard />
  </React.Suspense>
);

const LazyExtractionMonitorDashboard = () => (
  <React.Suspense fallback={<PageSkeleton />}>
    <ExtractionMonitorDashboard />
  </React.Suspense>
);

const LazyDataQualityDashboard = () => (
  <React.Suspense fallback={<PageSkeleton />}>
    <DataQualityDashboard />
  </React.Suspense>
);

const LazyAuditLogDashboard = () => (
  <React.Suspense fallback={<TableSkeleton />}>
    <AuditLogDashboard />
  </React.Suspense>
);

// Placeholder resource components (to be implemented)
const DatasetsList = () => <div>Datasets List</div>;

// API base URL from environment
const API_BASE = import.meta.env.VITE_API_URL || '/api';

// Notification alert interface
interface NotificationAlert {
  id: string;
  indicator_name: string;
  org_unit_name: string;
  severity: 'critical' | 'warning' | 'info';
  alert_type: string;
  detected_value: number;
  expected_value: number;
  deviation_score: number;
  detection_date: string;
}

/**
 * Notification Bell Component
 * Shows real-time anomaly alerts in a dropdown popover.
 * Also shows slide-in toast notifications from top-right when new alerts arrive.
 * Merges the old AlertToastStack (bottom-right) into the bell (top-right).
 */
const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [alerts, setAlerts] = useState<NotificationAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState<NotificationAlert[]>([]);
  const [hasNewPulse, setHasNewPulse] = useState(false);
  const [hoveredToastId, setHoveredToastId] = useState<string | null>(null);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const isFirstFetchRef = useRef(true);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const sendBrowserNotification = useCallback((alert: NotificationAlert) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const severity = alert.severity.toUpperCase();
        const n = new Notification(`[${severity}] ${alert.indicator_name}`, {
          body: `${alert.org_unit_name} — ${alert.detected_value} observed (expected ${alert.expected_value})`,
          icon: '/znphi_logo.jpg',
          tag: `alert-${alert.id}`,
          requireInteraction: alert.severity === 'critical',
        });
        n.onclick = () => {
          window.focus();
          const params = new URLSearchParams();
          params.set('disease', alert.indicator_name);
          params.set('district', alert.org_unit_name);
          navigate(`/ai/generated-alerts?${params.toString()}`);
          n.close();
        };
      } catch (err) {
        console.warn('Browser notification failed:', err);
      }
    }
  }, [navigate]);

  const fetchAlerts = useCallback(async () => {
    try {
      if (isFirstFetchRef.current) setLoading(true);
      const response = await fetch(`${API_BASE}/ai/alerts?days=30&limit=20`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        const mapped: NotificationAlert[] = (data.alerts || []).slice(0, 20).map((a: Record<string, unknown>) => ({
          id: String(a.id),
          indicator_name: String(a.indicator_name || 'Unknown'),
          org_unit_name: String(a.org_unit_name || 'Unknown'),
          severity: a.severity as 'critical' | 'warning' | 'info',
          alert_type: String(a.alert_type || 'spike'),
          detected_value: Number(a.detected_value) || 0,
          expected_value: Number(a.expected_value) || 0,
          deviation_score: Number(a.deviation_score) || 0,
          detection_date: String(a.detection_date || ''),
        }));
        setAlerts(mapped);

        // Detect new alerts — skip first fetch to avoid spamming on page load
        if (isFirstFetchRef.current) {
          mapped.forEach(a => seenIdsRef.current.add(a.id));
          isFirstFetchRef.current = false;
        } else {
          const newAlerts = mapped.filter(a => !seenIdsRef.current.has(a.id));
          if (newAlerts.length > 0) {
            newAlerts.forEach(a => {
              seenIdsRef.current.add(a.id);
              sendBrowserNotification(a);
            });
            setToasts(prev => [...newAlerts.slice(0, 3), ...prev].slice(0, 3));
            setHasNewPulse(true);
            setTimeout(() => setHasNewPulse(false), 3000);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60 * 1000); // Poll every 60s
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  // Auto-dismiss toasts after 8s — paused while hovering
  useEffect(() => {
    if (toasts.length === 0) return;
    if (hoveredToastId) return; // Pause dismiss while hovering
    const timer = setTimeout(() => {
      setToasts(prev => prev.slice(0, -1));
    }, 8000);
    return () => clearTimeout(timer);
  }, [toasts, hoveredToastId]);

  const dismissToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewAll = () => {
    handleClose();
    navigate('/ai/generated-alerts');
  };

  const handleAlertClick = (alert: NotificationAlert) => {
    handleClose();
    const params = new URLSearchParams();
    params.set('disease', alert.indicator_name);
    params.set('district', alert.org_unit_name);
    navigate(`/ai/generated-alerts?${params.toString()}`);
  };

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const totalCount = alerts.length;

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#dc2626';
      case 'warning': return '#ef7d00';
      default: return '#16a34a';
    }
  };

  const formatTimeAgo = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays > 0) return `${diffDays}d ago`;
      if (diffHours > 0) return `${diffHours}h ago`;
      return 'Just now';
    } catch {
      return '';
    }
  };

  return (
    <>
      {/* Toast notifications sliding in from top-right, below header */}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 72, md: 84 },
          right: 16,
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          pointerEvents: 'auto',
        }}
      >
        {toasts.map((toast) => {
          const color = getSeverityColor(toast.severity);
          return (
            <Slide key={toast.id} direction="left" in mountOnEnter unmountOnExit timeout={350}>
              <Paper
                elevation={8}
                onMouseEnter={() => setHoveredToastId(toast.id)}
                onMouseLeave={() => setHoveredToastId(null)}
                sx={{
                  pointerEvents: 'auto',
                  position: 'relative',
                  zIndex: 99999,
                  width: { xs: 'calc(100vw - 32px)', sm: 340 },
                  borderRadius: 2,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  borderLeft: `4px solid ${color}`,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                  bgcolor: hoveredToastId === toast.id ? alpha(color, 0.06) : 'background.paper',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { transform: 'translateX(-4px)', boxShadow: '0 12px 40px rgba(0,0,0,0.22)' },
                }}
                onClick={() => {
                  dismissToast(toast.id);
                  const tp = new URLSearchParams();
                  tp.set('disease', toast.indicator_name);
                  tp.set('district', toast.org_unit_name);
                  navigate(`/ai/generated-alerts?${tp.toString()}`);
                }}
              >
                <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <WarningIcon sx={{ fontSize: 18, color, mt: 0.25, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', lineHeight: 1.3 }}>
                      {toast.indicator_name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <LocationOnIcon sx={{ fontSize: 11, color: '#9ca3af' }} />
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        {toast.org_unit_name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <Chip
                        label={toast.severity}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: '0.6rem',
                          bgcolor: alpha(color, 0.12),
                          color,
                          fontWeight: 700,
                        }}
                      />
                      <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>
                        {toast.detected_value} vs {toast.expected_value} expected
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); dismissToast(toast.id); }}
                    sx={{ p: 0.25, color: '#9ca3af', '&:hover': { color: '#374151' } }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Paper>
            </Slide>
          );
        })}
      </Box>

      {/* Bell icon with pulse on new alerts */}
      <Tooltip title={`${totalCount} alerts (${criticalCount} critical)`}>
        <IconButton
          onClick={handleClick}
          sx={{
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
          }}
        >
          <Badge
            badgeContent={criticalCount > 0 ? criticalCount : totalCount}
            color={criticalCount > 0 ? 'error' : 'warning'}
            max={99}
          >
            <NotificationsIcon
              sx={{
                animation: hasNewPulse ? 'bellShake 0.5s ease-in-out 3' : 'none',
                '@keyframes bellShake': {
                  '0%, 100%': { transform: 'rotate(0deg)' },
                  '25%': { transform: 'rotate(15deg)' },
                  '75%': { transform: 'rotate(-15deg)' },
                },
              }}
            />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Popover dropdown — full alert list on bell click */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 480,
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', bgcolor: '#f8faf9' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0d5c2e' }}>
              Surveillance Alerts
            </Typography>
            <Chip
              label={`${totalCount} active`}
              size="small"
              sx={{ bgcolor: alpha('#ef7d00', 0.1), color: '#ef7d00', fontWeight: 600 }}
            />
          </Box>
        </Box>

        {/* Alerts List */}
        <List sx={{ p: 0, maxHeight: 360, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress size={24} sx={{ color: '#0d5c2e' }} />
            </Box>
          ) : alerts.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                No active alerts
              </Typography>
            </Box>
          ) : (
            alerts.map((alert) => {
              const color = getSeverityColor(alert.severity);
              const isPositive = alert.deviation_score > 0;
              const deviationPercent = alert.expected_value !== 0
                ? ((alert.detected_value - alert.expected_value) / alert.expected_value) * 100
                : alert.deviation_score * 100;

              return (
                <ListItem
                  key={alert.id}
                  onClick={() => handleAlertClick(alert)}
                  sx={{
                    cursor: 'pointer',
                    borderBottom: '1px solid #f3f4f6',
                    py: 1.5,
                    px: 2,
                    '&:hover': { bgcolor: alpha(color, 0.05) },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        bgcolor: alpha(color, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <WarningIcon sx={{ fontSize: 16, color }} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                          {alert.indicator_name}
                        </Typography>
                        <Chip
                          label={alert.severity}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            bgcolor: alpha(color, 0.1),
                            color,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <LocationOnIcon sx={{ fontSize: 12, color: '#6b7280' }} />
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>
                            {alert.org_unit_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#9ca3af', mx: 0.5 }}>•</Typography>
                          <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                            {formatTimeAgo(alert.detection_date)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                          {isPositive ? (
                            <TrendingUpIcon sx={{ fontSize: 12, color: '#dc2626' }} />
                          ) : (
                            <TrendingDownIcon sx={{ fontSize: 12, color: '#3b82f6' }} />
                          )}
                          <Typography variant="caption" sx={{ color: isPositive ? '#dc2626' : '#3b82f6', fontWeight: 600 }}>
                            {isPositive ? '+' : ''}{deviationPercent.toFixed(1)}%
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#9ca3af' }}>
                            vs {alert.expected_value.toFixed(1)}% baseline
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })
          )}
        </List>

        {/* Footer */}
        <Box sx={{ p: 1.5, borderTop: '1px solid #e5e7eb', bgcolor: '#f8faf9' }}>
          <Button
            fullWidth
            size="small"
            onClick={handleViewAll}
            sx={{
              color: '#0d5c2e',
              fontWeight: 600,
              '&:hover': { bgcolor: alpha('#0d5c2e', 0.08) },
            }}
          >
            View All Alerts
          </Button>
        </Box>
      </Popover>
    </>
  );
};

/**
 * User Profile Menu Component
 * Shows user avatar with dropdown for profile, settings, and logout
 * Includes password change and settings dialogs
 */
const UserProfileMenu = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const { data: identity, isLoading } = useGetIdentity();
  const logout = useLogout();
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  const handleOpenPasswordDialog = () => {
    handleClose();
    setPasswordDialogOpen(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordError('');
  };

  const handleOpenSettings = () => {
    handleClose();
    navigate('/security');
  };

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setIsChangingPassword(true);
    setPasswordError('');

    try {
      await apiClient.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setPasswordSuccess(true);
      setPasswordDialogOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 48 }}>
        <CircularProgress size={24} sx={{ color: 'white' }} />
      </Box>
    );
  }

  const initials = identity?.fullName
    ? identity.fullName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{
            ml: 2,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: '#ef7d00',
              fontSize: '1rem',
              fontWeight: 600,
              border: '2px solid rgba(255, 255, 255, 0.8)',
            }}
          >
            {initials}
          </Avatar>
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          id="account-menu"
          open={open}
          onClose={handleClose}
          PaperProps={{
            elevation: 4,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.2))',
              mt: 1.5,
              minWidth: 220,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#0d5c2e' }}>
              {identity?.fullName || 'User'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              {identity?.email || ''}
            </Typography>
            <Typography variant="caption" sx={{ color: '#999', textTransform: 'capitalize' }}>
              {identity?.role || 'User'}
            </Typography>
          </Box>
          <MenuItem onClick={handleOpenPasswordDialog} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <LockIcon fontSize="small" sx={{ color: '#0d5c2e' }} />
            </ListItemIcon>
            <ListItemText>Change Password</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); navigate('/mfa-setup'); }} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <SecurityIcon fontSize="small" sx={{ color: '#0d5c2e' }} />
            </ListItemIcon>
            <ListItemText>Two-step verification</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenSettings} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" sx={{ color: '#0d5c2e' }} />
            </ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { handleClose(); navigate('/help'); }} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <HelpOutlineIcon fontSize="small" sx={{ color: '#0d5c2e' }} />
            </ListItemIcon>
            <ListItemText>Help & Support</ListItemText>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: '#d32f2f' }}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: '#d32f2f' }} />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* Password Change Dialog */}
      <Dialog 
        open={passwordDialogOpen} 
        onClose={handleClosePasswordDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ 
          bgcolor: '#0d5c2e', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <LockIcon />
          Change Password
        </DialogTitle>
        <DialogContent sx={{ pt: 3, mt: 2 }}>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Current Password"
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                    size="small"
                  >
                    {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
            helperText="Minimum 8 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    size="small"
                  >
                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={confirmPassword !== '' && newPassword !== confirmPassword}
            helperText={confirmPassword !== '' && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button 
            onClick={handleClosePasswordDialog}
            disabled={isChangingPassword}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleChangePassword}
            variant="contained"
            disabled={isChangingPassword}
            sx={{ 
              bgcolor: '#0d5c2e',
              '&:hover': { bgcolor: '#094a24' }
            }}
          >
            {isChangingPassword ? <CircularProgress size={20} color="inherit" /> : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={passwordSuccess}
        autoHideDuration={4000}
        onClose={() => setPasswordSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setPasswordSuccess(false)} 
          severity="success"
          variant="filled"
        >
          Password changed successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

/**
 * Hamburger Menu Component for sidebar toggle
 */
const HamburgerMenu = () => {
  const [open, setOpen] = useSidebarState();

  return (
    <IconButton
      onClick={() => setOpen(!open)}
      sx={{
        color: 'white',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
      }}
      aria-label="Toggle sidebar"
    >
      <MenuIcon sx={{ fontSize: 28 }} />
    </IconButton>
  );
};

/**
 * Scroll to top on route change.
 * React Router v6 does not auto-scroll; this replicates the browser
 * behaviour users expect when clicking navigation links.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

/**
 * Redesigned Custom Layout with truly fixed header
 * Simple approach: Custom fixed header outside React Admin's Layout
 */
const CustomLayout = (props: any) => {
  const navigate = useNavigate();
  // The sidebar width below is applied with !important to beat react-admin's
  // own rule, which means it also beat the collapsed state and the hamburger
  // appeared to do nothing. Reading the state here lets the width follow it.
  const [sidebarOpen] = useSidebarState();
  // Start version checker and restore scroll position on mount
  useEffect(() => {
    startVersionChecker();
    restoreScrollPosition();
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      <ScrollToTop />
      {/* Custom Fixed Header - Outside React Admin control */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: { xs: 60, md: 72 },
          background: 'linear-gradient(135deg, #0d5c2e 0%, #094a24 100%)',
          boxShadow: '0 4px 12px rgba(13, 92, 46, 0.3)',
          borderBottom: '3px solid #ef7d00',
          zIndex: 1300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: { xs: '0 8px', md: '0 24px' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          {/* Left side - Hamburger Menu */}
          <HamburgerMenu />

          {/* Center - Coat of Arms, Title, ZNPHI Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 2, md: 3 },
              flex: 1,
            }}
          >
            {/* Coat of Arms - Left */}
            <Box
              component="img"
              src="/coa.png"
              alt="Zambia Coat of Arms"
              sx={{
                height: { xs: 36, md: 50 },
                width: 'auto',
                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.2))',
                display: { xs: 'none', sm: 'block' },
              }}
            />
            
            {/* Title with subtitle */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h5"
                sx={{
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: { xs: '0.95rem', sm: '1.15rem', md: '1.4rem' },
                  letterSpacing: '0.5px',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  lineHeight: 1.2,
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                  Public Health Security Information Management System - PHSIMS
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                  PHSIMS
                </Box>
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: { xs: '0.7rem', md: '0.85rem' },
                  fontWeight: 500,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  mt: 0.25,
                }}
              >
                Data Platform
              </Typography>
              {IS_DEV_INSTANCE && (
                <Box
                  sx={{
                    mt: 0.5,
                    px: 1,
                    py: 0.25,
                    borderRadius: 999,
                    bgcolor: 'warning.main',
                    color: 'warning.contrastText',
                    fontSize: { xs: '0.6rem', md: '0.7rem' },
                    fontWeight: 700,
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                  }}
                >
                  Dev Instance - Request production access from system admin
                </Box>
              )}
            </Box>

            {/* ZNPHI Logo - Right */}
            <Box
              component="img"
              src="/znphi_logo.jpg"
              alt="ZNPHI"
              sx={{
                height: { xs: 32, md: 44 },
                width: 'auto',
                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.2))',
                display: { xs: 'none', sm: 'block' },
              }}
            />
          </Box>
          
          {/* Right side - AI Chat, Notifications & User Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Ask the PHSIMS AI assistant a question">
              <Button
                onClick={() => navigate('/ai/chat')}
                startIcon={<AutoAwesomeIcon />}
                aria-label="Open PHSIMS AI Chat"
                sx={{
                  color: '#ffffff',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  lineHeight: 1,
                  borderRadius: 999,
                  minWidth: 0,
                  px: { xs: 1, sm: 1.75 },
                  py: 0.6,
                  border: '1px solid rgba(255,255,255,0.55)',
                  bgcolor: 'rgba(255,255,255,0.14)',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                  '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.75 }, ml: 0 },
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.26)',
                    borderColor: 'rgba(255,255,255,0.9)',
                  },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Ask AI
                </Box>
              </Button>
            </Tooltip>
            <NotificationBell />
            <UserProfileMenu />
          </Box>
        </Box>
      </Box>

      {/* Connection status overlay - shows during backend restarts */}
      <React.Suspense fallback={null}>
        <ConnectionStatusBanner />
      </React.Suspense>

      {/* Update banner - shows when new version detected, auto-reloads */}
      <React.Suspense fallback={null}>
        <UpdateBanner />
      </React.Suspense>

      {/* Main Content Area - fixed position between header and footer */}
      <Box
        sx={{
          position: 'fixed',
          top: { xs: 60, md: 72 },
          left: 0,
          right: 0,
          bottom: 64,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Layout
          {...props}
          appBar={() => null}
          menu={UnifiedMenu}
          sx={{
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            // Remove default Layout top spacing
            '& .RaLayout-appFrame': {
              marginTop: 0,
              height: '100%',
              minHeight: 0,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              overflowX: 'hidden',
            },
            '& .RaLayout-contentWithSidebar': {
              marginTop: 0,
              height: '100%',
              minHeight: 0,
              minWidth: 0,
              flex: 1,
              overflowX: 'hidden',
            },
            '& .RaSidebar-fixed': {
              width: sidebarOpen ? '320px !important' : '0px !important',
              top: { xs: '60px', md: '72px' },
              height: { xs: 'calc(100vh - 124px)', md: 'calc(100vh - 136px)' },
              borderRight: 'none',
              boxShadow: sidebarOpen
                ? '4px 0 20px rgba(13, 92, 46, 0.08), 1px 0 0 rgba(13, 92, 46, 0.1)'
                : 'none',
              background: 'linear-gradient(180deg, #ffffff 0%, #f8faf9 100%)',
              paddingTop: '8px',
              overflowY: sidebarOpen ? 'auto' : 'hidden',
              overflowX: 'hidden',
              position: 'relative',
              transition: 'width 200ms ease',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: '3px',
                background: 'linear-gradient(180deg, #0d5c2e 0%, #148a44 50%, #ef7d00 100%)',
                opacity: sidebarOpen ? 0.6 : 0,
              },
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(13, 92, 46, 0.3)',
                borderRadius: '3px',
              },
              '& .RaMenuItemLink-root': {
                borderRadius: '8px',
                margin: '2px 8px',
                padding: '10px 16px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(13, 92, 46, 0.08)',
                  transform: 'translateX(4px)',
                },
                '&.RaMenuItemLink-active': {
                  backgroundColor: 'rgba(13, 92, 46, 0.12)',
                  borderLeft: '4px solid #0d5c2e',
                  fontWeight: 600,
                },
              },
              '& .MuiListItemIcon-root': {
                minWidth: 40,
                color: '#0d5c2e',
              },
              '& .MuiTypography-root': {
                fontWeight: 500,
                fontSize: '0.95rem',
                whiteSpace: 'normal !important',
                wordBreak: 'break-word',
                overflow: 'visible !important',
                textOverflow: 'clip !important',
              },
              '& .MuiListItemText-primary': {
                whiteSpace: 'normal !important',
                wordBreak: 'break-word',
                lineHeight: 1.3,
              },
            },
            '& .RaLayout-content': {
              position: 'relative',
              padding: 0,
              backgroundColor: '#f1f5f9',
              height: '100%',
              minHeight: 0,
              minWidth: 0,
              width: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              borderLeft: '1px solid rgba(13, 92, 46, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              // Constrain all page root elements to content width
              '& > *': {
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
                overflowX: 'hidden',
              },
            },
            '& .RaSidebar-docked': {
              // Reserves the layout gap. Left at 320px while collapsed it would
              // hold an empty column open next to a hidden menu.
              width: sidebarOpen ? '320px !important' : '0px !important',
              minWidth: sidebarOpen ? undefined : '0 !important',
              borderRight: 'none',
              boxShadow: sidebarOpen ? '4px 0 20px rgba(13, 92, 46, 0.08)' : 'none',
              transition: 'width 200ms ease',
            },
          }}
        />
      </Box>

      {/* Fixed Footer */}
      <AppFooter variant="main" />

      {/* §9.3 Floating AI Chat — available on every page */}
      <React.Suspense fallback={null}>
        <FloatingChatButton />
      </React.Suspense>
    </Box>
  );
};

/**
 * React Admin content component wrapped inside browser router
 * Includes WebSocket for hot reload notifications
 */
const AdminContent = () => {
  const wsRef = React.useRef<WebSocket | null>(null);

  // Sign the user out after the configured period of inactivity.
  useIdleLogout();

  useEffect(() => {
    // Only enable hot reload WebSocket in development (localhost)
    const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (!isDev) {
      return; // Skip WebSocket in production
    }

    // Connect to WebSocket for hot reload notifications
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/ws/reload`;

    const connectWS = () => {
      try {
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          console.log('[WebSocket] Connected for hot reload');
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'build_complete' || data.type === 'reload') {
              console.log('[WebSocket] Build update detected, reloading page...');
              // Small delay to ensure backend is fully ready
              setTimeout(() => {
                window.location.reload();
              }, 500);
            }
          } catch (err) {
            console.error('[WebSocket] Failed to parse message:', err);
          }
        };

        ws.onerror = (error) => {
          console.error('[WebSocket] Error:', error);
        };

        ws.onclose = () => {
          console.log('[WebSocket] Disconnected, reconnecting in 5s...');
          
          // Reconnect after 5 seconds
          setTimeout(connectWS, 5000);
        };
      } catch (err) {
        console.error('[WebSocket] Failed to connect:', err);
        // Retry after 5 seconds
        setTimeout(connectWS, 5000);
      }
    };

    connectWS();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  return (
  <Admin
    title="PHSIMS Data Platform"
    dataProvider={dataProvider}
    authProvider={authProvider}
    loginPage={LoginPage}
    theme={zambiaTheme}
    layout={CustomLayout}
    dashboard={RoleBasedHome}
    // Wait for the auth check to resolve before rendering any page, so a
    // logged-out visitor is sent straight to login instead of briefly seeing
    // the dashboard shell flash by while checkAuth's /auth/me is in flight.
    requireAuth
  >
    {/* Data Sources Resource - Lazy loaded */}
    <Resource
      name="sources"
      list={LazySourcesDashboard}
      icon={StorageIcon}
      options={{ label: 'Data Sources' }}
    />
    
    {/* Datasets Resource */}
    <Resource
      name="datasets"
      list={DatasetsList}
      icon={DatasetIcon}
      options={{ label: 'Datasets' }}
    />
    
    {/* Extraction Monitor - 360° Extraction Monitoring Dashboard - Lazy loaded */}
    <Resource
      name="monitoring"
      list={LazyExtractionMonitorDashboard}
      icon={MonitorHeartIcon}
      options={{ label: 'Extraction Monitor' }}
    />
    
    {/* Data Quality Dashboard - Lazy loaded */}
    <Resource
      name="quality"
      list={LazyDataQualityDashboard}
      icon={AssessmentIcon}
      options={{ label: 'Data Quality' }}
    />
    
    {/* Audit Log - Lazy loaded */}
    <Resource
      name="audit"
      list={LazyAuditLogDashboard}
      icon={HistoryIcon}
      options={{ label: 'Audit Log' }}
    />

    {/* Custom Routes for Specialized Dashboards - All require authentication */}
    {/* Role enforcement: RequireRole checks role hierarchy + page permissions */}
    {/* <Authenticated> only checks login; RequireRole also checks role/permissions */}
    <CustomRoutes>
      {/* Data Management Routes — editor or higher */}
      <Route path="/sources" element={<RequireRole pagePath="/sources"><LazyPage><SourcesDashboard /></LazyPage></RequireRole>} />
      <Route path="/sources/:sourceId" element={<RequireRole pagePath="/sources"><LazyForm><SourceDetailsRouter /></LazyForm></RequireRole>} />
      <Route path="/uploads" element={<RequireRole pagePath="/uploads"><LazyPage><UploadsPage /></LazyPage></RequireRole>} />
      <Route path="/harmonization" element={<RequireRole pagePath="/harmonization"><LazyTable><OrgUnitHarmonization /></LazyTable></RequireRole>} />
      {/* SQL Explorer — admin/editor raw SQL IDE */}
      <Route path="/sql-explorer" element={<RequireRole pagePath="/sql-explorer"><LazyPage><SqlExplorer /></LazyPage></RequireRole>} />
      {/* Data Studio — unified data exploration */}
      <Route path="/data-studio" element={<RequireRole pagePath="/data-studio"><LazyPage><DataStudioPage /></LazyPage></RequireRole>} />
      {/* Approval step for the public page. Stays behind a role gate permanently —
          the API additionally requires editor rights to approve or reject. */}
      <Route path="/infodemic-review" element={<RequireRole pagePath="/data-studio"><LazyPage><InfodemicReviewPage /></LazyPage></RequireRole>} />
      
      {/* Administration Routes — admin only */}
      <Route path="/config" element={<RequireRole pagePath="/config"><LazyForm><ConfigDashboard /></LazyForm></RequireRole>} />
      <Route path="/disa-config" element={<RequireRole pagePath="/disa-config"><LazyForm><DisaConfigDashboard /></LazyForm></RequireRole>} />
      <Route path="/users" element={<Navigate to="/security" replace />} />
      <Route path="/roles" element={<Navigate to="/security" replace />} />
      <Route path="/security" element={<RequireRole pagePath="/security"><LazyTable><SecurityManagement /></LazyTable></RequireRole>} />
      <Route path="/platform-usage" element={<Navigate to="/dashboards/gateway" replace />} />
      <Route path="/backup" element={<RequireRole pagePath="/backup"><LazyPage><BackupDashboard /></LazyPage></RequireRole>} />
      <Route path="/audit" element={<RequireRole pagePath="/audit"><LazyPage><AuditLogDashboard /></LazyPage></RequireRole>} />
      <Route path="/alerts" element={<RequireRole pagePath="/alerts"><LazyPage><AlertsConfiguration /></LazyPage></RequireRole>} />
      <Route path="/emails" element={<RequireRole pagePath="/emails"><LazyPage><EmailManagementPage /></LazyPage></RequireRole>} />
      <Route path="/credentials" element={<RequireRole pagePath="/credentials"><LazyTable><CredentialManagement /></LazyTable></RequireRole>} />
      <Route path="/system-config" element={<RequireRole pagePath="/system-config"><LazyForm><SystemConfigurationPage /></LazyForm></RequireRole>} />

      {/* Dashboard Routes — operations visible to editor+, monitoring to viewer+ */}
      <Route path="/operations" element={<RequireRole pagePath="/operations"><LazyPage><EnhancedDashboard /></LazyPage></RequireRole>} />
      <Route path="/dashboards/data-quality" element={<RequireRole pagePath="/quality"><LazyPage><DataQualityDashboard /></LazyPage></RequireRole>} />
      <Route path="/quality" element={<RequireRole pagePath="/quality"><LazyPage><DataQualityDashboard /></LazyPage></RequireRole>} />
      <Route path="/dashboards/monitoring" element={<RequireRole pagePath="/dashboards/monitoring"><LazyPage><ExtractionMonitorDashboard /></LazyPage></RequireRole>} />
      <Route path="/dashboards/pipeline" element={<RequireRole pagePath="/dashboards/pipeline"><LazyPage><PipelineDashboard /></LazyPage></RequireRole>} />
      <Route path="/dashboards/system-health" element={<RequireRole pagePath="/dashboards/system-health"><LazyPage><SystemHealthDashboard /></LazyPage></RequireRole>} />
      <Route path="/dashboards/pulse" element={<RequireRole pagePath="/dashboards/pulse"><LazyPage><SystemPulsePage /></LazyPage></RequireRole>} />
      <Route path="/dashboards/redis" element={<RequireRole pagePath="/dashboards/redis"><LazyPage><RedisMonitoringDashboard /></LazyPage></RequireRole>} />
      <Route path="/dashboards/services" element={<RequireRole pagePath="/dashboards/services"><LazyPage><ServicesDashboard /></LazyPage></RequireRole>} />
      <Route path="/dashboards/errors" element={<RequireRole pagePath="/dashboards/errors"><LazyPage><ErrorsDashboard /></LazyPage></RequireRole>} />
      <Route path="/dashboards/api" element={<Navigate to="/dashboards/gateway" replace />} />
      <Route path="/dashboards/gateway" element={<RequireRole pagePath="/dashboards/gateway"><LazyPage><APIGatewayDashboard /></LazyPage></RequireRole>} />
      <Route path="/dashboards/billing" element={<Navigate to="/dashboards/marketplace" replace />} />
      <Route path="/dashboards/federation" element={<RequireRole pagePath="/dashboards/federation"><LazyPage><FederationDashboard /></LazyPage></RequireRole>} />
      <Route path="/dashboards/marketplace" element={<RequireRole pagePath="/dashboards/marketplace"><LazyPage><MarketplacePage /></LazyPage></RequireRole>} />
      <Route path="/dashboards/developer-portal" element={<RequireRole pagePath="/dashboards/developer-portal"><LazyPage><DeveloperPortalPage /></LazyPage></RequireRole>} />
      <Route path="/dashboards/custom-endpoints" element={<RequireRole pagePath="/dashboards/custom-endpoints"><LazyPage><CustomEndpointsPage /></LazyPage></RequireRole>} />
      <Route path="/dashboards/api-platform" element={<Navigate to="/dashboards/developer-portal" replace />} />
      <Route path="/dashboards/marketplace/consumers/:consumerId" element={<RequireRole pagePath="/dashboards/marketplace"><LazyPage><ConsumerDetailPage /></LazyPage></RequireRole>} />
      <Route path="/dashboards/marketplace/products/:productId" element={<RequireRole pagePath="/dashboards/marketplace"><LazyPage><ProductDetailPage /></LazyPage></RequireRole>} />
      <Route path="/dashboards/schedule" element={<RequireRole pagePath="/dashboards/schedule"><LazyPage><ScheduleManagerDashboard /></LazyPage></RequireRole>} />
      <Route path="/dashboards/pools" element={<RequireRole pagePath="/dashboards/pools"><LazyPage><PoolDashboard /></LazyPage></RequireRole>} />
      <Route path="/dashboards/outliers" element={<RequireRole pagePath="/dashboards/outliers"><LazyPage><OutliersPage /></LazyPage></RequireRole>} />
      <Route path="/dashboards/logs" element={<RequireRole pagePath="/dashboards/logs"><LazyTable><LogsDashboard /></LazyTable></RequireRole>} />
      
      {/* Infrastructure Routes — admin only */}
      <Route path="/infrastructure/terminal" element={<RequireRole pagePath="/infrastructure/terminal"><LazyPage><WebTerminal /></LazyPage></RequireRole>} />
      <Route path="/infrastructure/files" element={<RequireRole pagePath="/infrastructure/files"><LazyPage><FileBrowser /></LazyPage></RequireRole>} />
      <Route path="/infrastructure/events" element={<RequireRole pagePath="/infrastructure/events"><LazyPage><DockerEvents /></LazyPage></RequireRole>} />
      <Route path="/infrastructure/images" element={<RequireRole pagePath="/infrastructure/images"><LazyPage><ImageManagement /></LazyPage></RequireRole>} />
      
      {/* AI Analytics Platform — viewer+ with ai_analytics feature */}
      <Route path="/ai" element={<RequireRole pagePath="/ai/home"><LazyPage><ExecutiveOverviewPage /></LazyPage></RequireRole>} />
      <Route path="/ai/home" element={<RequireRole pagePath="/ai/home"><LazyPage><ExecutiveOverviewPage /></LazyPage></RequireRole>} />
      <Route path="/ai/surveillance-dashboard" element={<RequireRole pagePath="/ai/home"><LazyPage><SurveillanceDashboardPage /></LazyPage></RequireRole>} />
      <Route path="/ai/chat" element={<RequireRole pagePath="/ai/chat"><LazyChat><ModernChat /></LazyChat></RequireRole>} />
      <Route path="/ai/anomalies" element={<RequireRole pagePath="/ai/anomalies"><LazyPage><AnomalyDetection /></LazyPage></RequireRole>} />
      <Route path="/ai/trends" element={<RequireRole pagePath="/ai/trends"><LazyPage><TrendAnalysis /></LazyPage></RequireRole>} />
      <Route path="/ai/predictions" element={<RequireRole pagePath="/ai/predictions"><LazyPage><Predictions /></LazyPage></RequireRole>} />
      <Route path="/ai/dashboard" element={<RequireRole pagePath="/ai/dashboard"><LazyPage><PublicDashboard /></LazyPage></RequireRole>} />
      <Route path="/ai/reports" element={<RequireRole pagePath="/ai/reports"><LazyPage><Reports /></LazyPage></RequireRole>} />
      <Route path="/ai/explorer" element={<RequireRole pagePath="/ai/explorer"><LazyTable><DataExplorer /></LazyTable></RequireRole>} />
      <Route path="/ai/dictionary" element={<RequireRole pagePath="/ai/dictionary"><LazyTable><DataDictionary /></LazyTable></RequireRole>} />
      <Route path="/ai/report-builder" element={<RequireRole pagePath="/ai/report-builder"><LazyPage><ReportBuilder /></LazyPage></RequireRole>} />
      <Route path="/ai/data-visualizer" element={<RequireRole pagePath="/ai/data-visualizer"><LazyPage><DataVisualizer /></LazyPage></RequireRole>} />
      <Route path="/ai/event-visualizer" element={<RequireRole pagePath="/ai/event-visualizer"><LazyPage><EventVisualizer /></LazyPage></RequireRole>} />
      <Route path="/ai/surveillance" element={<RequireRole pagePath="/ai/home"><LazyPage><SurveillanceDashboardPage /></LazyPage></RequireRole>} />
      <Route path="/ai/surveillance-hub" element={<RequireRole pagePath="/ai/home"><LazyPage><SurveillanceDashboardPage /></LazyPage></RequireRole>} />
      <Route path="/ai/alert-config" element={<RequireRole pagePath="/ai/alert-config"><LazyPage><AlertConfigPage /></LazyPage></RequireRole>} />
      <Route path="/ai/transparency" element={<RequireRole pagePath="/ai/transparency"><LazyPage><ModelTransparencyPage /></LazyPage></RequireRole>} />
      <Route path="/ai/thresholds" element={<RequireRole pagePath="/ai/alert-config"><LazyPage><AlertConfigPage /></LazyPage></RequireRole>} />
      <Route path="/ai/generated-alerts" element={<RequireRole pagePath="/ai/alerts"><LazyPage><ThresholdAlertProgramPage /></LazyPage></RequireRole>} />
      <Route path="/ai/investigations" element={<RequireRole pagePath="/ai/investigations"><LazyPage><InvestigationWorkspace /></LazyPage></RequireRole>} />
      <Route path="/ai/hotspots" element={<RequireRole pagePath="/ai/hotspots"><LazyPage><HotspotAnalysisPage /></LazyPage></RequireRole>} />
      <Route path="/ai/responses" element={<RequireRole pagePath="/ai/home"><LazyPage><ResponseTrackerPage /></LazyPage></RequireRole>} />
      <Route path="/ai/excess-mortality" element={<RequireRole pagePath="/ai/mortality"><LazyPage><ExcessMortalityPage /></LazyPage></RequireRole>} />
      <Route path="/ai/bulletins" element={<RequireRole pagePath="/ai/bulletin"><LazyPage><WeeklyBulletinPage /></LazyPage></RequireRole>} />
      <Route path="/ai/bulletin-generator" element={<RequireRole pagePath="/ai/bulletin"><LazyPage><BulletinGeneratorPage /></LazyPage></RequireRole>} />
      <Route path="/ai/lab-analytics" element={<RequireRole pagePath="/ai/lab"><LazyPage><LabAnalyticsPage /></LazyPage></RequireRole>} />
      <Route path="/ai/settings" element={<RequireRole pagePath="/ai/settings"><LazyForm><AIHomeDashboard /></LazyForm></RequireRole>} />
      <Route path="/ai/knowledge" element={<RequireRole pagePath="/ai/knowledge"><LazyForm><AIKnowledgePage /></LazyForm></RequireRole>} />
      <Route path="/ai/engine-overview" element={<RequireRole pagePath="/ai/transparency"><LazyPage><ModelTransparencyPage /></LazyPage></RequireRole>} />
      <Route path="/ai/disease-intelligence" element={<RequireRole pagePath="/ai/home"><LazyPage><SurveillanceDashboardPage /></LazyPage></RequireRole>} />
      <Route path="/ai/signals" element={<RequireRole pagePath="/ai/signals"><LazyPage><SignalDashboardPage /></LazyPage></RequireRole>} />
      <Route path="/ai/analytics-explorer" element={<RequireRole pagePath="/ai/analytics-explorer"><LazyPage><AnalyticsExplorerPage /></LazyPage></RequireRole>} />
      <Route path="/ai/agent-monitor" element={<RequireRole pagePath="/ai/agent-monitor"><LazyPage><AgentMonitorPage /></LazyPage></RequireRole>} />
      
      {/* §9.7 Revised Sidebar — new canonical routes */}
      <Route path="/ai/disease" element={<RequireRole pagePath="/ai/disease"><LazyPage><DiseaseDeepDivePage /></LazyPage></RequireRole>} />
      <Route path="/ai/disease/:disease" element={<RequireRole pagePath="/ai/disease"><LazyPage><DiseaseDeepDivePage /></LazyPage></RequireRole>} />
      <Route path="/ai/alerts" element={<RequireRole pagePath="/ai/alerts"><LazyPage><ThresholdAlertProgramPage /></LazyPage></RequireRole>} />
      <Route path="/ai/bulletin" element={<RequireRole pagePath="/ai/bulletin"><LazyPage><BulletinGeneratorPage /></LazyPage></RequireRole>} />
      <Route path="/ai/lab" element={<RequireRole pagePath="/ai/lab"><LazyPage><LabAnalyticsPage /></LazyPage></RequireRole>} />
      <Route path="/ai/climate" element={<RequireRole pagePath="/ai/climate"><LazyPage><ClimateDiseasePage /></LazyPage></RequireRole>} />
      <Route path="/ai/climate-advanced" element={<Navigate to="/ai/climate" replace />} />
      <Route path="/ai/weather" element={<RequireRole pagePath="/ai/weather"><LazyPage><WeatherAnalyticsPage /></LazyPage></RequireRole>} />
      <Route path="/ai/one-health" element={<RequireRole pagePath="/ai/one-health"><LazyPage><OneHealthPage /></LazyPage></RequireRole>} />
      <Route path="/ai/malaria" element={<RequireRole pagePath="/ai/malaria"><LazyPage><MalariaIntelligencePage /></LazyPage></RequireRole>} />
      <Route path="/ai/mortality" element={<RequireRole pagePath="/ai/mortality"><LazyPage><ExcessMortalityPage /></LazyPage></RequireRole>} />
      <Route path="/ai/catalog" element={<RequireRole pagePath="/ai/catalog"><LazyPage><AlgorithmCatalogPage /></LazyPage></RequireRole>} />
      <Route path="/ai/performance" element={<RequireRole pagePath="/ai/performance"><LazyPage><EngineMonitoringPage /></LazyPage></RequireRole>} />
      
      {/* Documentation Routes */}
      <Route path="/docs/api-reference" element={<Authenticated><LazyPage><ApiReferencePage /></LazyPage></Authenticated>} />
      <Route path="/docs" element={<Authenticated><LazyPage><TechnicalDocs /></LazyPage></Authenticated>} />
      <Route path="/docs/*" element={<Authenticated><LazyPage><TechnicalDocs /></LazyPage></Authenticated>} />
      <Route path="/help" element={<Authenticated><LazyPage><HelpPage /></LazyPage></Authenticated>} />
    </CustomRoutes>

    {/* Rendered outside the Layout: the launch screen is a full-bleed
        composition shown on a projector, so the sidebar, the fixed footer and
        the floating chat button all have to be absent rather than merely
        scrolled past. noLayout is react-admin's mechanism for that.

        Still Authenticated: it exists to open /ai/home, which is itself gated,
        so a signed-out visitor would only reach a login prompt one click
        later. Signed in, play goes straight through. */}
    <CustomRoutes noLayout>
      <Route path="/launch" element={<Authenticated><LazyPage><LaunchPage /></LazyPage></Authenticated>} />

      {/* The disease bulletin is written to be read by the public and
          screenshotted, so it renders without the sidebar, app footer and chat
          button rather than as another console screen.

          Still role-gated: the figures have not yet been checked against the
          institute's own published bulletin. Publishing is dropping the
          RequireRole wrapper here and setting the infodemic router to "none"
          in router_loader.py — the page itself needs no further change. */}
      <Route
        path="/disease-status"
        element={<RequireRole pagePath="/data-studio"><LazyPage><DiseaseStatusPage /></LazyPage></RequireRole>}
      />
    </CustomRoutes>
  </Admin>
  );
};

/**
 * Main Application Component with BrowserRouter for clean URLs
 * Uses createBrowserRouter to eliminate the /#/ hash from URLs
 */
const browserRouter = createBrowserRouter([
  {
    path: "/reset-password",
    element: <PasswordResetPage />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/change-password",
    element: <ChangePasswordPage />,
  },
  {
    path: "/mfa-setup",
    element: <MfaSetupPage />,
  },
  // Developer Portal is now a separate service at port 3000
  {
    path: "*",
    element: <AdminContent />,
  },
]);

/* LaunchConfetti sits outside the router on purpose: the burst starts on
   /launch and has to keep running after the route changes to /ai/home, which
   it could not do from inside a page that unmounts. */
const App = () => (
  <>
    <RouterProvider router={browserRouter} />
    <LaunchConfetti />
  </>
);

export default App;
